import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, Check } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';

// Aviso de "ação necessária na sua integração" (internal/advisory na API).
// Só chega aqui o que EXIGE mexer no código do cliente — nunca changelog, que
// vive no widget de release notes. Por isso o banner não é dispensável por
// sessão como o de cobrança: some quando a pessoa marca como lido, e só então.
interface Advisory {
  id: string;
  title: string;
  impact: string;
  action: string;
  link?: string;
}

export function AdvisoryBanner() {
  const t = useT();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['advisories'],
    queryFn: async (): Promise<Advisory[]> => {
      const r = await apiFetch('GET', '/advisories');
      if (!r.ok) return [];
      return ((r.data as { advisories?: Advisory[] })?.advisories ?? []) as Advisory[];
    },
    staleTime: 5 * 60_000,
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await apiFetch('POST', `/advisories/${encodeURIComponent(id)}/read`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['advisories'] }),
  });

  if (!data?.length) return null;

  return (
    <div className="mb-4 flex flex-col gap-3">
      {data.map((a) => (
        <div
          key={a.id}
          className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-amber-900 dark:text-amber-200">{a.title}</p>
            <p className="mt-0.5 text-muted-foreground">{a.impact}</p>
            <p className="mt-1.5">
              <span className="font-medium">{t('advisories.whatToDo')}: </span>
              <span className="text-muted-foreground">{a.action}</span>
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {a.link && (
                <a
                  href={a.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline dark:text-amber-300"
                >
                  {t('advisories.details')} <ExternalLink className="size-3" />
                </a>
              )}
              <button
                onClick={() => markRead.mutate(a.id)}
                disabled={markRead.isPending}
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <Check className="size-3" /> {t('advisories.dismiss')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
